import { FullwidthTopLayoutNoBottomRail } from '@zoopla/layout/page';
import { PageProps, SearchType } from './getServerSideProps';
import { SearchBar } from './lib';
import { Breadcrumbs } from '@zoopla-eng/zds-components/breadcrumbs';
import { AgentCard } from './components/AgentCard';
import { Pagination } from '@zoopla-eng/zds-components/pagination';
import {
    createEmailAgentsPath,
    getResultsString,
    pageNumberToURL,
    lsrpToAfEcommerce,
    resultsToAgentsList
} from './helpers';
import { Head, HeadProps } from '@zoopla/layout/head';
import { SortFilter } from './components/SortFilter';
import { Link } from '@zoopla-eng/zds-components/link';
import { ErrorBanner } from './components/ErrorBanner';
import {
    trackSegmentAndGAEvent,
    usePageSegmentTracking
} from '@zoopla/tracking';
import { Grid } from '@zoopla-eng/zds-components/grid';
import React from 'react';
import {
    AgentsSearchConnection,
    EmailAllResultEcommerce
} from './graphql.generated';
import dynamic from 'next/dynamic';
import { ComponentProps } from 'react';
import { graphQLWrapper } from '@zoopla/apollo';
import {
    cardListStyle,
    pageContainerStyle,
    resultsCountStyle,
    sortContainerStyle
} from './FindAgentResults.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes';

const EmailAllAgentsWrapper = dynamic<
    ComponentProps<
        typeof import('@zoopla/find-agents/email-agents').EmailAllAgentsWrapper
    >
>(
    () =>
        import('@zoopla/find-agents/email-agents').then(
            (module) => module.EmailAllAgentsWrapper
        ),
    {
        ssr: false
    }
);

export const ERROR_TITLE = 'Sorry, we cannot find any results for your search';
export const ERROR_TEXT =
    'Check your spelling and try your search again. If you experience any difficulties or need further assistance, please';
export const CONTACT_US_TEXT = 'contact us';
export const CONTACT_US_HREF = 'https://help.zoopla.co.uk/hc/en-gb';
export const NO_RESULTS_TITLE =
    'No results found. We were unable to locate any results matching your search criteria.';
export const NO_RESULTS_TEXT =
    'Please amend your search options or try searching again.';

export const SEARCH_TYPE_NAME: Record<SearchType, string> = {
    'estate-agents': 'Estate agents',
    'letting-agents': 'Letting agents',
    all: 'Estate agents and letting agents'
};

function noResultsTitle(locationDisplayName?: string | null) {
    if (locationDisplayName)
        return `Sorry, we can’t find any estate agents and letting agents in ${locationDisplayName}`;
    return NO_RESULTS_TITLE;
}

function getTitle(
    searchType: SearchType,
    locationDisplayName: string | undefined | null,
    companyName: string | null
) {
    if (!locationDisplayName) {
        return `${SEARCH_TYPE_NAME[searchType]} matching “${companyName}”`;
    }
    return `${SEARCH_TYPE_NAME[searchType]} in ${locationDisplayName}`;
}

const FindAgentsHead: React.FC<
    Partial<HeadProps> & {
        initialLocation: string;
        locationDisplayName?: AgentsSearchConnection['locationDisplayName'];
        searchType?: SearchType;
    }
> = ({ index, initialLocation, locationDisplayName, searchType }) => {
    const url = new URL(initialLocation, 'https://zoopla.co.uk');
    const canonicalPath = url.pathname;
    const displayName = locationDisplayName ?? 'your area';

    let type = 'Estate Agents and Letting Agents';
    if (searchType === 'estate-agents') type = 'Estate Agents';
    if (searchType === 'letting-agents') type = 'Letting Agents';

    return (
        <Head
            pageTitle={`Find ${type} in ${displayName} | Zoopla`}
            pageDescription={`Find and compare ${type.toLowerCase()} in ${displayName}. Contact the best agents for you and get a free valuation of your property with Zoopla.`}
            canonicalPath={canonicalPath}
            index={index}
        />
    );
};

export const FindAgentResults = (props: PageProps) => {
    const { taxonomy, ecommerce = undefined } = props;
    usePageSegmentTracking(taxonomy.page, taxonomy, ecommerce);

    if (props.type === 'error') {
        return (
            <>
                <FindAgentsHead
                    index={() => false}
                    initialLocation={props.initialLocation}
                />
                <FullwidthTopLayoutNoBottomRail
                    topRail={<div />}
                    main={
                        <div className={pageContainerStyle}>
                            <SearchBar
                                initialLocation={props.initialLocation}
                            />
                            <ErrorBanner title={ERROR_TITLE}>
                                <p>
                                    {ERROR_TEXT}{' '}
                                    <Link href={CONTACT_US_HREF}>
                                        {CONTACT_US_TEXT}
                                    </Link>
                                    .
                                </p>
                            </ErrorBanner>
                        </div>
                    }
                />
            </>
        );
    }

    const { initialLocation, data, searchType, companyName } = props;
    const { agents, breadcrumbs, isLoggedIn } = data;
    const noResults = agents.results.length === 0;

    const onSubmittedTrackingHandler = (
        lsrpEcommerce: EmailAllResultEcommerce,
        valuationToggle: boolean
    ) => {
        trackSegmentAndGAEvent(
            'Agent Lead Submit',
            {
                eventCategory: 'email_all',
                eventAction: 'contact_agents',
                eventLabel: `valuation_toggle_${String(valuationToggle)}`
            },
            taxonomy.page,
            taxonomy,
            lsrpToAfEcommerce(lsrpEcommerce, agents.results)
        );
    };

    return (
        <>
            <FindAgentsHead
                index={() => !noResults && agents.pagination.pageNumber === 1}
                initialLocation={initialLocation}
                locationDisplayName={agents.locationDisplayName}
                searchType={searchType}
            />
            <FullwidthTopLayoutNoBottomRail
                topRail={<div />}
                main={
                    <div className={pageContainerStyle}>
                        <SearchBar initialLocation={initialLocation} />
                        <Breadcrumbs items={breadcrumbs} />
                        {noResults ? (
                            <ErrorBanner
                                title={noResultsTitle(
                                    agents.locationDisplayName
                                )}
                            >
                                {NO_RESULTS_TEXT}
                            </ErrorBanner>
                        ) : (
                            <>
                                <h1
                                    className={fontGroups.mug}
                                    id="find-agents-results-title"
                                >
                                    {getTitle(
                                        searchType,
                                        agents.locationDisplayName,
                                        companyName
                                    )}
                                </h1>

                                <div className={sortContainerStyle}>
                                    <Grid.Item area="results">
                                        <p
                                            className={`${resultsCountStyle} ${fontGroups.ladle}`}
                                        >
                                            {getResultsString(
                                                agents.pagination.pageNumber,
                                                agents.total
                                            )}
                                        </p>
                                    </Grid.Item>
                                    <Grid.Item area="sort">
                                        <SortFilter />
                                    </Grid.Item>
                                </div>
                                <ul className={cardListStyle}>
                                    {agents.results.map(
                                        (agentBranch, index) => (
                                            <AgentCard
                                                key={agentBranch.id}
                                                agentBranch={agentBranch}
                                                agentEcommerceData={
                                                    ecommerce?.impressions[
                                                        index
                                                    ]
                                                }
                                                taxonomy={taxonomy}
                                            />
                                        )
                                    )}
                                </ul>
                                <Pagination
                                    currentPage={agents.pagination.pageNumber}
                                    totalPages={agents.pagination.pageTotal}
                                    pageNumberToURL={(n) =>
                                        pageNumberToURL(initialLocation, n)
                                    }
                                />
                                <EmailAllAgentsWrapper
                                    path={createEmailAgentsPath(
                                        initialLocation
                                    )}
                                    searchType={
                                        initialLocation.includes(
                                            'letting-agents'
                                        )
                                            ? 'to-rent'
                                            : 'for-sale'
                                    }
                                    location={
                                        agents.locationDisplayName ??
                                        'your area'
                                    }
                                    isUserLoggedIn={isLoggedIn}
                                    createAlert={null}
                                    agentsList={resultsToAgentsList(
                                        agents.results
                                    )}
                                    onSubmitted={onSubmittedTrackingHandler}
                                />
                            </>
                        )}
                    </div>
                }
            />
        </>
    );
};
export const FindAgentsResultsPage = graphQLWrapper(FindAgentResults);
